import React, { useState, useRef, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import Icon from '../../../components/AppIcon';
import api from '../../../api/axios';

const getFromTo = (dateRange, customStart, customEnd) => {
  const now = new Date();
  let from = new Date();
  let to = new Date();
  if (dateRange === '7d') {
    from.setDate(now.getDate() - 6);
  } else if (dateRange === '30d') {
    from.setDate(now.getDate() - 29);
  } else if (dateRange === 'custom' && customStart && customEnd) {
    from = new Date(customStart);
    to = new Date(customEnd);
  } else {
    return null;
  }
  from.setHours(0, 0, 0, 0);
  to.setHours(23, 59, 59, 999);
  const fmt = (d) => d.toISOString().split('.')[0];
  return { from: fmt(from), to: fmt(to) };
};

const f2 = (v) => parseFloat(Number(v).toFixed(2));

const GRANULARITY_MAP = { day: 'DAY', week: 'WEEK', month: 'MONTH' };

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload?.length) {
    const d = payload?.[0]?.payload;
    return (
      <div className="bg-card border border-border rounded-xl p-4 shadow-lg min-w-[200px]">
        <p className="text-sm font-semibold text-foreground mb-3">{label}</p>
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-4">
            <span className="text-xs text-muted-foreground">NPS Score</span>
            <span className="text-sm font-bold data-text" style={{ color: d?.nps >= 50 ? '#059669' : d?.nps >= 0 ? '#D97706' : '#DC2626' }}>{f2(d?.nps)}</span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-xs text-muted-foreground">Responses</span>
            <span className="text-sm font-medium data-text text-foreground">{d?.responses?.toLocaleString()}</span>
          </div>
          <div className="border-t border-border pt-2 mt-2 grid grid-cols-3 gap-2 text-center">
            <div>
              <div className="text-xs font-bold text-green-600">{f2(d?.promoters)}%</div>
              <div className="text-xs text-muted-foreground">Pro</div>
            </div>
            <div>
              <div className="text-xs font-bold text-yellow-600">{f2(d?.passives)}%</div>
              <div className="text-xs text-muted-foreground">Pas</div>
            </div>
            <div>
              <div className="text-xs font-bold text-red-600">{f2(d?.detractors)}%</div>
              <div className="text-xs text-muted-foreground">Det</div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

const CustomDateDropdown = ({ dateRange, setDateRange, customStart, setCustomStart, customEnd, setCustomEnd }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref?.current && !ref?.current?.contains(e?.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const options = [
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: 'custom', label: 'Custom' },
  ];

  const getLabel = () => {
    if (dateRange === '7d') return 'Last 7 days';
    if (dateRange === '30d') return 'Last 30 days';
    if (dateRange === 'custom' && customStart && customEnd) return `${customStart} → ${customEnd}`;
    return 'Custom';
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 flex items-center gap-1.5 ${
          open || dateRange !== '7d' ?'bg-card text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
        }`}
      >
        <Icon name="Calendar" size={12} color="#6B7280" />
        <span>{getLabel()}</span>
        <Icon name="ChevronDown" size={11} color="#6B7280" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1.5 z-50 bg-card border border-border rounded-xl shadow-lg p-2 min-w-[220px]">
          {options?.map((opt) => (
            <button
              key={opt?.value}
              onClick={() => {
                setDateRange(opt?.value);
                if (opt?.value !== 'custom') setOpen(false);
              }}
              className={`w-full text-left px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                dateRange === opt?.value
                  ? 'bg-primary text-primary-foreground'
                  : 'text-foreground hover:bg-muted'
              }`}
            >
              {opt?.label}
            </button>
          ))}

          {dateRange === 'custom' && (
            <div className="mt-2 pt-2 border-t border-border space-y-2 px-1">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Start Date</label>
                <input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e?.target?.value)}
                  className="w-full px-2 py-1.5 text-xs border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">End Date</label>
                <input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e?.target?.value)}
                  min={customStart}
                  className="w-full px-2 py-1.5 text-xs border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
              <button
                onClick={() => { if (customStart && customEnd) setOpen(false); }}
                disabled={!customStart || !customEnd}
                className="w-full py-1.5 rounded-lg text-xs font-medium bg-primary text-primary-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
              >
                Apply
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const NPSTrendChart = () => {
  const [granularity, setGranularity] = useState('day');
  const [dateRange, setDateRange] = useState('7d');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const range = getFromTo(dateRange, customStart, customEnd);
    if (!range) return;
    setLoading(true);
    api.get('/api/v1/nps-analytics/trend', {
      params: { ...range, granularity: GRANULARITY_MAP[granularity] }
    })
      .then((res) => {
        const points = (res.data?.data?.data || []).map((p) => ({
          period: p.period,
          nps: p.nps,
          responses: p.responses,
          promoters: p.promotersPercent,
          passives: p.passivesPercent,
          detractors: p.detractorsPercent,
        }));
        setChartData(points);
      })
      .catch((err) => console.error('Failed to fetch NPS trend:', err))
      .finally(() => setLoading(false));
  }, [dateRange, customStart, customEnd, granularity]);

  const granularityOptions = [
    { value: 'day', label: 'Day' },
    { value: 'week', label: 'Week' },
    { value: 'month', label: 'Month' },
  ];

  const generateInsight = (data) => {
    if (!data || data.length < 2) return null;
    const insights = [];

    // Find biggest drop between consecutive periods
    let maxDrop = 0, dropFrom = null, dropTo = null;
    for (let i = 1; i < data.length; i++) {
      const diff = data[i - 1].nps - data[i].nps;
      if (diff > maxDrop) {
        maxDrop = diff;
        dropFrom = data[i - 1];
        dropTo = data[i];
      }
    }
    if (maxDrop >= 5) {
      insights.push(`NPS dropped from ${dropFrom.nps} → ${dropTo.nps} between ${dropFrom.period} and ${dropTo.period}.`);
    }

    // Find highest detractor spike
    const maxDet = data.reduce((best, d) => d.detractors > best.detractors ? d : best, data[0]);
    if (maxDet.detractors > 0) {
      insights.push(`Highest detractor rate of ${maxDet.detractors}% was on ${maxDet.period}.`);
    }

    // Overall trend
    const first = data[0].nps, last = data[data.length - 1].nps;
    const diff = last - first;
    if (Math.abs(diff) >= 3) {
      insights.push(`Overall NPS ${diff > 0 ? 'improved' : 'declined'} by ${Math.abs(diff)} points (${first} → ${last}) over this period.`);
    }

    // Best period
    const best = data.reduce((b, d) => d.nps > b.nps ? d : b, data[0]);
    if (best.nps > 0) {
      insights.push(`Best NPS score of ${best.nps} was recorded on ${best.period}.`);
    }

    return insights.length > 0 ? insights.slice(0, 2).join(' ') : null;
  };

  const insight = generateInsight(chartData);

  return (
    <div className="bg-card border border-border rounded-xl p-6 mb-6" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="text-base font-semibold text-foreground">NPS Trend</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Score over time with response volume</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Date Range with Custom support */}
          <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
            <button
              onClick={() => setDateRange('7d')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                dateRange === '7d' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Last 7 days
            </button>
            <button
              onClick={() => setDateRange('30d')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                dateRange === '30d' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Last 30 days
            </button>
            <CustomDateDropdown
              dateRange={dateRange}
              setDateRange={setDateRange}
              customStart={customStart}
              setCustomStart={setCustomStart}
              customEnd={customEnd}
              setCustomEnd={setCustomEnd}
            />
          </div>

          {/* Granularity Toggle */}
          <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
            {granularityOptions?.map((opt) => (
              <button
                key={opt?.value}
                onClick={() => setGranularity(opt?.value)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                  granularity === opt?.value
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {opt?.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* Legend */}
      <div className="flex items-center gap-4 mb-4">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-0.5 bg-blue-600 rounded" />
          <span className="text-xs text-muted-foreground">NPS Score</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-0.5 border-t-2 border-dashed border-gray-400" />
          <span className="text-xs text-muted-foreground">Zero line</span>
        </div>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : chartData.length === 0 ? (
        <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">No trend data available for this period.</div>
      ) : <ResponsiveContainer width="100%" height={280}>
        <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
          <XAxis
            dataKey="period"
            tick={{ fontSize: 12, fill: '#6B7280', fontFamily: 'JetBrains Mono' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 12, fill: '#6B7280', fontFamily: 'JetBrains Mono' }}
            axisLine={false}
            tickLine={false}
            domain={[-20, 80]}
          />
          <Tooltip content={<CustomTooltip />} />
          <ReferenceLine y={0} stroke="#9CA3AF" strokeDasharray="4 4" />
          <Line
            type="monotone"
            dataKey="nps"
            stroke="#1E3A8A"
            strokeWidth={2.5}
            dot={{ fill: '#1E3A8A', r: 4, strokeWidth: 2, stroke: '#fff' }}
            activeDot={{ r: 6, fill: '#1E3A8A', stroke: '#fff', strokeWidth: 2 }}
          />
        </LineChart>
      </ResponsiveContainer>}
      {/* Insight bar */}
      {insight && (
        <div className="mt-4 flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-100">
          <Icon name="Lightbulb" size={14} color="#1E3A8A" className="mt-0.5 shrink-0" />
          <p className="text-xs text-blue-800">
            <span className="font-semibold">Insight:</span> {insight}
          </p>
        </div>
      )}
    </div>
  );
};

export default NPSTrendChart;
